import { useState } from "react";
import { Routes, Route, Navigate, useOutlet, useLocation, useNavigate, Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  Box, Sheet, Typography, Button, IconButton, CircularProgress, Drawer,
  List, ListItem, ListItemButton, ListItemDecorator,
  Stack,
} from "@mui/joy";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";

import AdminAuthProvider, { useAdminAuth } from "./AdminAuthContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import StaffPage from "./pages/StaffPage";
import LogsPage from "./pages/LogsPage";
import OrdersPage from "./pages/OrdersPage";
import ActionsPage from "./pages/ActionsPage";
import { pageVariants } from "./components/motion";

// Логотип в сайдбаре. При необходимости замените на свой ассет
// (например, импортом из src/assets или на локальный путь).
export const LOGO_URL = "/logo_black.png";


const hideOnError = (e) => { e.currentTarget.style.display = "none"; };

function FullscreenLoader() {
  return (
    <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CircularProgress />
    </Box>
  );
}

function ProtectedRoute() {
  const { staff, loading } = useAdminAuth();
  const location = useLocation();
  if (loading) return <FullscreenLoader />;
  if (!staff) return <Navigate to="/admin/login" replace state={{ from: location }} />;
  return <AdminLayout />;
}

// Анимированный Outlet: вход справа, выход вниз с прозрачностью.
function AnimatedOutlet() {
  const location = useLocation();
  const element = useOutlet();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ height: "100%" }}
      >
        {element}
      </motion.div>
    </AnimatePresence>
  );
}

function SidebarBrand() {
  return (
    <Box sx={{ gap: 1.25, px: 1, mb: 2 }}>
      <Box
        component="img"
        src={LOGO_URL}
        alt="MagicBoard"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        sx={{ height: 34, width: "auto", objectFit: "contain" }}
      />
    </Box>
  );
}

function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden", bgcolor: "background.body" }}>
      {/* Десктоп-сайдбар (фиксированной высоты, свой скролл) */}
      <Sheet
        variant="soft"
        sx={{
          width: 250, flexShrink: 0, height: "100vh", overflowY: "auto",
          display: { xs: "none", md: "block" },
          borderRight: "1px solid", borderColor: "divider",
        }}
      >
        <SidebarContent />
      </Sheet>

      {/* Мобильный сайдбар — выезжает по кнопке в хедере */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        slotProps={{ content: { sx: { width: 264, p: 0 } } }}
      >
        <SidebarContent onNavigate={() => setMobileOpen(false)} />
      </Drawer>

      {/* Правая колонка: мобильный хедер + прокручиваемый контент */}
      <Box sx={{ flex: 1, height: "100vh", display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Sheet
          variant="soft"
          sx={{
            display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1,
            px: 1.5, py: 1, flexShrink: 0,
            borderBottom: "1px solid", borderColor: "divider",
          }}
        >
          <IconButton variant="plain" color="neutral" onClick={() => setMobileOpen(true)} aria-label="Меню">
            <MenuRoundedIcon />
          </IconButton>
          <Box component="img" src={LOGO_URL} alt="MagicBoard" onError={hideOnError}
               sx={{ height: 26, width: "auto", objectFit: "contain" }} />
        </Sheet>

        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", width: "100%" }}>
            <AnimatedOutlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Brand() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1, mb: 2 }}>
      <Box component="img" src={LOGO_URL} alt="MagicBoard" onError={hideOnError}
           sx={{ height: 34, width: "auto", objectFit: "contain" }} />
    </Box>
  );
}


// Внутренности сайдбара — переиспользуются в десктоп-панели и мобильном Drawer.
function SidebarContent({ onNavigate }) {
  const { staff, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const nav = [
    { to: "/admin", label: "Дашборд", icon: <BarChartRoundedIcon />, exact: true },
    { to: "/admin/logs", label: "Логи", icon: <ArticleRoundedIcon /> },
    { to: "/admin/orders", label: "Заказы", icon: <ReceiptLongRoundedIcon /> },
  ];
  if (staff?.role === "admin" || staff?.role === "superadmin") {
    nav.push({ to: "/admin/actions", label: "Доп. действия", icon: <BuildRoundedIcon /> });
  }
  if (staff?.role === "superadmin") {
    nav.push({ to: "/admin/staff", label: "Сотрудники", icon: <GroupRoundedIcon /> });
  }

  const isActive = (n) => (n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to));
  const handleNav = () => { if (onNavigate) onNavigate(); };
  const doLogout = async () => { await logout(); handleNav(); navigate("/admin/login", { replace: true }); };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "stretch", p: 2 }}>
      <Brand />

      <Typography level="body-xs" color="neutral"
        sx={{ px: 1, mb: 0.5, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
        Разделы
      </Typography>
      <List size="sm" sx={{ gap: 0.5 }}>
        {nav.map((n) => {
          const active = isActive(n);
          return (
            <ListItem key={n.to}>
              <ListItemButton
                component={Link}
                to={n.to}
                selected={active}
                onClick={handleNav}
                sx={{
                  borderRadius: "10px",
                  fontWeight: active ? 700 : 500,
                  ...(active && {
                    bgcolor: "primary.softBg",
                    color: "primary.plainColor",
                    boxShadow: "inset 3px 0 0 var(--joy-palette-primary-solidBg)",
                  }),
                }}
              >
                <ListItemDecorator sx={{ color: active ? "primary.plainColor" : "neutral.400" }}>
                  {n.icon}
                </ListItemDecorator>
                {n.label}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Sheet
        variant="solid"
        sx={{
          borderRadius: "14px", p: 1.25, mb: 1.25, flexDirection: "column",
          display: "flex", alignItems: "stretch", gap: 1.25,
        }}
      >
        <Stack direction={"row"} sx={{ gap: 1.25 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              bgcolor: "primary.800", color: "primary.solidColor", fontWeight: 700,
            }}
          >
            {(staff?.full_name || staff?.email || "?").trim().charAt(0).toUpperCase()}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography level="body-sm" sx={{ fontWeight: 600, color: 'white' }} noWrap>
              {staff?.full_name || staff?.email}
            </Typography>
            <Typography level="body-xs" color="neutral" noWrap sx={{ color: 'white' }}>{staff?.role_label}</Typography>
          </Box>
        </Stack>
        <Button variant="soft" color="neutral" startDecorator={<LogoutRoundedIcon />} onClick={doLogout}>
          Выйти
        </Button>
      </Sheet>
    </Box>
  );
}

export default function AdminApp() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <AdminAuthProvider>
      <Routes location={location} key={location.pathname}>
        <Route path="login" element={<LoginPage />} />
        <Route element={<ProtectedRoute />}>
          <Route index element={<DashboardPage />} />
          <Route path="logs" element={<LogsPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="actions" element={<ActionsPage />} />
          <Route path="staff" element={<StaffPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}